import { propertyType } from '@/components/core/async-data-table/type';
import { useSearchProductVariants } from '@/services/product-service';
import {
  FilterRequest,
  SearchProp,
  SortConfig,
  VariantStockSearchItem,
} from '@/types/product-type';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface VariantStats {
  totalVariants: number;
}

interface VariantContextValue {
  // Variants data (flat list from API)
  variants: VariantStockSearchItem[];
  isLoading: boolean;
  error: Error | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;
    isEmpty: boolean;
    isFirst: boolean;
    isLast: boolean;
  };
  stats: VariantStats;

  // Pagination controls
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortConfig: SortConfig[];
  setSortConfig: (sorts: SortConfig[]) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;

  // Generic property search (for FilterHeader pattern)
  // Only product, variant, and sale_code (SKU) filters are enabled
  propSearch: propertyType[];
  setPropSearch: (props: propertyType[]) => void;

  // Refresh
  refresh: () => void;

  // Column sorting
  handleColumnSort: (field: string) => void;
}

const VariantContext = createContext<VariantContextValue | undefined>(undefined);

/**
 * Allowed filter property names for the variant context.
 * Only product name, variant name, and sale code (SKU) filters are permitted.
 */
const ALLOWED_FILTER_PROPS = new Set(['productName', 'variantName', 'saleCode']);

export function VariantProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [propSearch, setPropSearchRaw] = useState<propertyType[]>([]);

  // Only allow product, variant, and sale_code (SKU) filters
  const setPropSearch = useCallback((props: propertyType[]) => {
    const filtered = props.filter((p) => ALLOWED_FILTER_PROPS.has(p.propName));
    setPropSearchRaw(filtered);
  }, []);

  // Build filter request matching backend format
  const filterRequest: FilterRequest | null = useMemo(() => {
    const searchProps: SearchProp[] = [];

    // Merge generic propSearch values (from FilterHeader pattern)
    // Only product, variant, and sale_code filters are applied
    for (const prop of propSearch) {
      if (prop.value && prop.value.toString().trim() !== '') {
        searchProps.push({
          propName: prop.propName,
          value: prop.value.toString(),
        });
      }
    }

    // Determine sortBy and ordinal from sortConfig
    // Default to p.name ascending if no sort config
    let sortBy = 'p.name';
    let ordinal: 'asc' | 'desc' = 'asc';

    if (sortConfig.length > 0) {
      const firstSort = sortConfig[0];
      sortBy = firstSort.field;
      ordinal = firstSort.direction;
    }

    // Normalize search query for case-insensitive search
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return {
      filterText: normalizedQuery || null,
      searchProp: searchProps.length > 0 ? searchProps : null,
      page,
      size: pageSize,
      sortBy,
      ordinal,
    };
  }, [searchQuery, sortConfig, page, pageSize, propSearch]);

  // Fetch variants (flat list from API)
  const {
    items: variants,
    isLoading,
    error,
    totalElements,
    totalPages,
    isEmpty,
    isFirst,
    isLast,
    currentPage,
    refresh,
  } = useSearchProductVariants(filterRequest);

  // Calculate stats
  const stats = useMemo(() => {
    return {
      totalVariants: totalElements,
    };
  }, [totalElements]);

  // Handle column sort - cycles through: not sorted -> asc -> desc -> not sorted
  const handleColumnSort = useCallback((field: string) => {
    setSortConfig((prev) => {
      const existingIndex = prev.findIndex((sort) => sort.field === field);
      const newSorts = [...prev];

      if (existingIndex >= 0) {
        const existingSort = newSorts[existingIndex];
        if (existingSort.direction === 'asc') {
          // Change to desc
          newSorts[existingIndex] = { field, direction: 'desc' };
          return newSorts;
        } else {
          // Remove sort
          newSorts.splice(existingIndex, 1);
          return newSorts;
        }
      } else {
        // Add new sort with asc direction
        return [...newSorts, { field, direction: 'asc' as const }];
      }
    });
    // Reset to first page when sorting changes
    setPage(0);
  }, []);

  const value: VariantContextValue = useMemo(
    () => ({
      variants,
      isLoading,
      error: error || null,
      pagination: {
        currentPage,
        pageSize,
        totalPages,
        totalElements,
        isEmpty,
        isFirst,
        isLast,
      },
      stats,
      searchQuery,
      setSearchQuery,
      sortConfig,
      setSortConfig,
      page,
      setPage,
      pageSize,
      setPageSize,
      propSearch,
      setPropSearch,
      refresh,
      handleColumnSort,
    }),
    [
      variants,
      isLoading,
      error,
      currentPage,
      pageSize,
      totalPages,
      totalElements,
      isEmpty,
      isFirst,
      isLast,
      stats,
      searchQuery,
      sortConfig,
      page,
      propSearch,
      setPropSearch,
      refresh,
      handleColumnSort,
    ],
  );

  return <VariantContext.Provider value={value}>{children}</VariantContext.Provider>;
}

export function useVariantContext() {
  const context = useContext(VariantContext);
  if (context === undefined) {
    throw new Error('useVariantContext must be used within a VariantProvider');
  }
  return context;
}
